import type { AdminSummary, AssessmentInput, Preview, ReportResponse } from "./types";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim().replace(/\/$/, "") ?? "";
const API_UNAVAILABLE_MESSAGE = import.meta.env.DEV
  ? "The CareerCompass API is not reachable. Start both services from the project root with `pnpm dev`, confirm http://localhost:8080/health opens, then try again."
  : "CareerCompass is temporarily unable to reach its report service. Please try again in a moment.";

export const ACCESS_PASS_STORAGE_KEY = "careercompass-access-pass";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...init?.headers }
    });
  } catch {
    throw new Error(API_UNAVAILABLE_MESSAGE);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? (response.status >= 500 ? API_UNAVAILABLE_MESSAGE : "Something went wrong. Please try again."));
  return body as T;
}

export const api = {
  health: () => request<{ ok: boolean; service: string }>("/health"),
  createAssessment: (input: AssessmentInput) =>
    request<{ reportId: string; assessmentId: string; preview: Preview; accessPass?: ReportResponse["accessPass"] }>("/api/assessments", {
      method: "POST", body: JSON.stringify(input)
    }),
  getReport: (reportId: string) => {
    const accessPass = localStorage.getItem(ACCESS_PASS_STORAGE_KEY);
    return request<ReportResponse>(`/api/reports/${reportId}`, accessPass ? { headers: { "X-Access-Pass": accessPass } } : undefined);
  },
  createPayment: (reportId: string) =>
    request<{
      paymentId: string;
      provider: "razorpay" | "manual";
      razorpay?: { keyId: string; orderId: string; amount: number; currency: string; name: string; description: string };
      message?: string;
      accessPassToken: string;
      maxUses: 3;
    }>("/api/payments/create", {
      method: "POST", body: JSON.stringify({ reportId, plan: "detailed_report" })
    }),
  verifyRazorpayPayment: (input: { razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string }) =>
    request<{ status: "paid" | "pending" }>("/api/payments/razorpay/verify", {
      method: "POST", body: JSON.stringify(input)
    }),
  publicStats: () => request<{ reportsGenerated: number }>("/api/stats/public"),
  submitFeedback: (input: { reportId: string; rating: number; testimonial: string; permissionToUsePublicly: boolean }) =>
    request<{ id: string }>("/api/feedback", { method: "POST", body: JSON.stringify(input) }),
  adminSummary: (password: string) =>
    request<AdminSummary>("/api/admin/summary", { headers: { "X-Admin-Password": password } }),
  unlockReport: (reportId: string, password: string) =>
    request<{ unlocked: boolean }>(`/api/admin/reports/${reportId}/unlock`, {
      method: "POST", body: "{}", headers: { "X-Admin-Password": password }
    })
};
